/**
 * Signing in with Microsoft (ADR 0093), and the two names for one thing.
 *
 * The product says **Microsoft**. Supabase's provider key is **`azure`** — the name Microsoft's own
 * identity service had before it was Azure AD and then Entra ID — and that string is what
 * `signInWithOAuth` takes, what `/auth/v1/settings` reports under `external`, and what the project
 * dashboard calls the toggle. One constant, one place, so nothing has to remember which name goes
 * where.
 *
 * ## Why this is a separate door from `signInWithSSO`
 *
 * FlowZa already has single sign-on: `signInWithSSO({ domain })` sends a person to their own
 * organization's SAML provider, which the organization registers through the wizard (ADR 0065).
 * That is **per-tenant federation** and it needs Supabase's SAML add-on on the project.
 *
 * This is the other kind: ONE Entra application registration, owned by FlowZa, multi-tenant, so
 * anybody with a Microsoft work, school or personal account can sign in without their organization
 * configuring anything. It needs no add-on. The two are not alternatives — a customer who has
 * registered SAML uses their own provider, and everybody else can still arrive this way.
 *
 * ## What a Microsoft sign-in does NOT decide
 *
 * Which organization the person lands in. That is the bootstrap path's answer and it is the same
 * for every sign-in method: an invitation first, then the organization that has VERIFIED the domain
 * of their confirmed address, then an organization of their own. A Microsoft identity is a way
 * through the door, not a claim about who they work for.
 */

/** Supabase's key for Microsoft Entra ID. Not a display string — never show this to a person. */
export const MICROSOFT_PROVIDER = 'azure'

/** What the product calls it, in one place so a button and a sentence cannot disagree. */
export const MICROSOFT_LABEL = 'Microsoft'

/**
 * The scopes the redirect asks for, and why each one.
 *
 *   * `openid` — the identity token itself.
 *   * `email` — the address, which is what the verified-domain join reads to decide the
 *     organization. Without it a first sign-in cannot land anywhere but a personal organization.
 *   * `profile` — the display name, so the console is not addressing somebody by their user id.
 *   * `offline_access` — the refresh token. Without it the session ends when the access token does
 *     and somebody is signed out mid-signature.
 *
 * Deliberately NOT any `Files`, `Mail` or `Sites` scope. A sign-in that asked for a mailbox would
 * put a consent screen in front of the person that an administrator would be right to refuse, and
 * FlowZa's storage and mailbox connectors ask for those separately, per connection, when somebody
 * actually connects one (ADR 0070).
 */
export const MICROSOFT_SCOPES = 'openid email profile offline_access'

/**
 * A Supabase OAuth failure, as a sentence.
 *
 * Two of these are worth telling apart, and both read as "it did not work" without this:
 *
 *   * **The provider is not enabled on the project.** Supabase answers "Unsupported provider" and
 *     the fix is a configuration step nobody signing in can take. The button is normally hidden in
 *     this case — the login page asks the project which providers are enabled before offering one —
 *     but a project whose provider is turned off between that answer and the click lands here.
 *   * **The redirect is not on the allow-list.** Supabase refuses to send a person to a URL the
 *     project does not know, which is the mechanism that stops an open redirect. The fix is also a
 *     configuration step, and the sentence says which one.
 *
 * Everything else is one message on purpose: the failures a person can do something about are a
 * cancelled consent screen and a blocked popup, and both are "try again".
 */
export function describeMicrosoftFailure(message: string | null | undefined): string {
  const said = (message ?? '').toLowerCase()
  if (said.includes('unsupported provider') || said.includes('provider is not enabled')) {
    return `Signing in with ${MICROSOFT_LABEL} is not switched on for this deployment yet. Use your email and password, or ask whoever runs FlowZa to enable it.`
  }
  if (said.includes('redirect') && (said.includes('not allowed') || said.includes('invalid'))) {
    return `${MICROSOFT_LABEL} sent you back to an address this deployment does not recognise. Whoever runs FlowZa needs to add this site to the allowed redirect list.`
  }
  return `We could not start a ${MICROSOFT_LABEL} sign-in. Try again, or use your email and password.`
}
